<template>
  <main v-if="resetToken" class="access-screen" :style="homeBackgroundStyle">
    <section class="access-panel" aria-labelledby="password-heading">
    <h1 id="password-heading">Set your Uchecking password</h1>
    <p>Use 12 to 128 characters. This link expires after one hour and can only be used once.</p>
    <p v-if="error" role="alert">{{ error }}</p>
    <form @submit.prevent="resetPassword">
      <label>New password <input v-model="password" type="password" autocomplete="new-password" minlength="12" maxlength="128" required /></label>
      <label>Confirm password <input v-model="confirmPassword" type="password" autocomplete="new-password" minlength="12" maxlength="128" required /></label>
      <button :disabled="busy" type="submit">Set password</button>
    </form>
    </section>
  </main>
  <template v-else-if="access && access.user">
    <div v-if="access.mode !== 'local'" class="access-bar">
      {{ access.user.name }} · {{ access.user.role }}
      <button @click="signOut">Sign out</button>
    </div>
    <slot />
  </template>
  <main v-else class="access-screen" :style="homeBackgroundStyle">
    <section class="access-panel" aria-labelledby="login-heading">
    <h1 id="login-heading">Uchecking Points Management</h1>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-if="message" role="status">{{ message }}</p>
    <p v-else-if="!access">Checking access...</p>
    <form v-if="access?.mode === 'private'" @submit.prevent="signInEmail">
      <p>Private access. Contact the administrator to request an account.</p>
      <label>Email <input v-model="email" type="email" autocomplete="username" required /></label>
      <label>Password <input v-model="password" type="password" autocomplete="current-password" required /></label>
      <button :disabled="busy" type="submit">Sign in</button>
      <button v-if="access.emailConfigured" :disabled="busy || !email" type="button" @click="forgotPassword">Send password reset link</button>
    </form>
    <template v-else-if="access?.mode === 'entra'">
      <p>Sign in with an authorized corporate account.</p>
      <button :disabled="busy" @click="signIn">Sign in with Microsoft</button>
    </template>
    <button v-if="error" @click="check">Try again</button>
    </section>
  </main>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { access } from './services/access'
import { homeBackgroundStyle } from './utils/homeBackground'
const router = useRouter()
const error = ref('')
const message = ref('')
const resetToken = ref(window.location.pathname === '/set-password' ? new URLSearchParams(window.location.hash.slice(1)).get('token') : '')
const confirmPassword = ref('')
const busy = ref(false)
const email = ref('')
const password = ref('')
let timer
async function check() {
  try {
    error.value = ''
    const response = await fetch('/api/access', { cache: 'no-store' })
    if (!response.ok) throw new Error('Unable to check access.')
    access.value = await response.json()
  } catch (err) { access.value = null; error.value = err.message }
}
async function authPost(path, body) {
  const response = await fetch(`/api/auth/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
  })
  if (!response.ok) throw new Error(response.status === 429 ? 'Too many attempts. Please wait a minute.' : 'Unable to sign in. Check your credentials and permissions.')
  return response.json()
}
async function signIn() {
  busy.value = true
  try {
    const data = await authPost('sign-in/social', { provider: 'microsoft', callbackURL: window.location.origin })
    if (!data.url) throw new Error('Microsoft did not return a sign-in URL.')
    window.location.assign(data.url)
  } catch (err) { error.value = err.message } finally { busy.value = false }
}
async function signInEmail() {
  busy.value = true
  error.value = ''
  try {
    await authPost('sign-in/email', { email: email.value, password: password.value, rememberMe: false })
    password.value = ''
    await check()
    if (!access.value?.user) error.value = 'This account is not authorized. Contact the administrator.'
  } catch (err) { error.value = err.message } finally { password.value = ''; busy.value = false }
}
async function signOut() {
  try { await authPost('sign-out', {}); window.location.reload() }
  catch (err) { error.value = err.message; access.value = null }
}
async function forgotPassword() {
  busy.value = true
  error.value = ''
  try {
    await authPost('request-password-reset', { email: email.value, redirectTo: `${window.location.origin}/set-password` })
    message.value = 'If this email belongs to an enabled account, a password link will be sent.'
  } catch { error.value = 'Unable to send the link. Please wait a few minutes or contact the administrator.' }
  finally { busy.value = false }
}
async function resetPassword() {
  if (password.value !== confirmPassword.value) { error.value = 'Passwords do not match.'; return }
  busy.value = true
  error.value = ''
  try {
    await authPost('reset-password', { token: resetToken.value, newPassword: password.value })
    window.location.replace('/')
  } catch { error.value = 'Unable to set password. The link may have expired or already been used. Request a new link.' }
  finally { password.value = ''; confirmPassword.value = ''; busy.value = false }
}
onMounted(() => {
  router.isReady().then(() => {
    if (resetToken.value) window.history.replaceState(null, '', '/set-password')
  })
  check(); timer = setInterval(check, 60000)
})
onUnmounted(() => clearInterval(timer))
</script>

<style scoped>
.access-screen {
  min-height: 100vh;
  min-height: 100svh;
  display: grid;
  place-items: center;
  padding: clamp(1rem, 4vw, 3rem);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.access-panel {
  width: 100%;
  max-width: 520px;
  padding: clamp(1.25rem, 4vw, 2rem);
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 14px;
  background: rgba(7, 18, 40, 0.82);
  color: #edf3ff;
  box-shadow: 0 18px 48px rgba(7, 18, 40, 0.28);
  backdrop-filter: blur(3px);
}
.access-panel h1 { color: #fff; font-size: clamp(1.5rem, 4vw, 2rem); line-height: 1.25; margin-bottom: 1rem; }
.access-panel p { line-height: 1.5; margin: .75rem 0; }
.access-panel label { font-weight: 600; }
.access-panel input { font: inherit; }
.access-panel button {
  width: 100%;
  margin: .5rem 0 0;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  background: transparent;
  color: #fff;
  font: inherit;
  font-weight: 600;
}
.access-panel button[type="submit"] { background: var(--uefa-accent); color: #091a3b; border-color: var(--uefa-accent); }
.access-panel button:disabled { opacity: .6; cursor: not-allowed; }
.access-panel [role="alert"] { color: #ffd5d5; }
.access-panel [role="status"] { color: #bfeaff; }
.access-bar { padding: .5rem 1rem; text-align: right; }
button { padding: .6rem 1rem; cursor: pointer; margin-left: .5rem; }
label { display: block; margin: 1rem 0; }
input { display: block; width: 100%; box-sizing: border-box; padding: .7rem; margin-top: .4rem; }
</style>
